import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Play,
  RefreshCw,
  Database,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetBranchesQuery,
  useGetRepositoryByIdQuery,
  useCreateBranchMutation,
  useDeleteBranchMutation,
  useTriggerIndexMutation,
  useTriggerFullReprocessMutation,
  useDropIndexMutation,
  useGetIndexStatusQuery,
} from '@/store/api/apiSlice';
import { asList } from '@/store/api/daytonaApi';
import type { Branch } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

export function BranchesPage() {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Branch | null>(null);
  const [statusBranch, setStatusBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState({ name: '', gitBranchName: '' });

  const { data: repo } = useGetRepositoryByIdQuery(repoId!);
  const { data: branches, isLoading } = useGetBranchesQuery(repoId!);

  const [createBranch, { isLoading: isCreating }] = useCreateBranchMutation();
  const [deleteBranch, { isLoading: isDeleting }] = useDeleteBranchMutation();
  const [triggerIndex, { isLoading: isIndexing }] = useTriggerIndexMutation();
  const [triggerFullReprocess, { isLoading: isReprocessing }] = useTriggerFullReprocessMutation();
  const [dropIndex, { isLoading: isDropping }] = useDropIndexMutation();

  const branchList = asList<Branch>(branches);

  const columns: ColumnDef<Branch, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          {row.original.isDefault && (
            <Badge variant="secondary" className="mt-0.5 text-xs">
              default
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'gitBranchName',
      header: 'Git Branch',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.gitBranchName}</span>
      ),
    },
    {
      accessorKey: 'neonBranchId',
      header: 'Neon Branch',
      cell: ({ row }) =>
        row.original.neonBranchId ? (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.neonBranchId.slice(0, 12)}…
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'lastIndexStatus',
      header: 'Index Status',
      cell: ({ row }) => <StatusBadge status={row.original.lastIndexStatus} />,
    },
    {
      accessorKey: 'lastIndexedAt',
      header: 'Last Indexed',
      cell: ({ row }) =>
        row.original.lastIndexedAt ? (
          <span className="text-xs" title={format(new Date(row.original.lastIndexedAt), 'PPpp')}>
            {formatDistanceToNow(new Date(row.original.lastIndexedAt), { addSuffix: true })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const branch = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); handleTriggerIndex(branch); }}
              disabled={isIndexing}
              title="Index Now"
            >
              <Play className="h-4 w-4 text-green-600" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); handleFullReprocess(branch); }}
              disabled={isReprocessing}
              title="Full Reprocess"
            >
              <RefreshCw className="h-4 w-4 text-blue-600" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); handleDropIndex(branch); }}
              disabled={isDropping}
              title="Drop Index"
            >
              <Database className="h-4 w-4 text-orange-600" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); setStatusBranch(branch); }}
              title="View index status"
            >
              <Info className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); setDeleteTarget(branch); }}
              title="Delete branch"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  function openCreate() {
    setFormData({ name: '', gitBranchName: '' });
    setModalOpen(true);
  }

  async function handleCreate() {
    if (!repoId) return;
    await createBranch({ repoId, body: formData }).unwrap();
    setModalOpen(false);
  }

  async function handleDelete() {
    if (!deleteTarget || !repoId) return;
    await deleteBranch({ repoId, branchId: deleteTarget._id }).unwrap();
    setDeleteTarget(null);
  }

  async function handleTriggerIndex(branch: Branch) {
    if (!repoId) return;
    try {
      await triggerIndex({ repoId, branchId: branch._id }).unwrap();
    } catch {
      // Error handled by RTK Query
    }
  }

  async function handleFullReprocess(branch: Branch) {
    if (!repoId) return;
    try {
      await triggerFullReprocess({ repoId, branchId: branch._id }).unwrap();
    } catch {
      // Error handled by RTK Query
    }
  }

  async function handleDropIndex(branch: Branch) {
    if (!repoId) return;
    try {
      await dropIndex({ repoId, branchId: branch._id }).unwrap();
    } catch {
      // Error handled by RTK Query
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/repositories')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{repo?.name ?? 'Branches'}</h1>
          <p className="text-sm text-muted-foreground">
            {repo?.gitUrl ? (
              <span className="font-mono">{repo.gitUrl}</span>
            ) : (
              'Manage branches and indexing'
            )}
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Branch
        </Button>
      </div>

      {/* Data table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : branchList.length === 0 ? (
            <EmptyState
              title="No branches found"
              description="Add a branch to start indexing"
              action={
                <Button onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Branch
                </Button>
              }
            />
          ) : (
            <DataTable columns={columns} data={branchList} />
          )}
        </CardContent>
      </Card>

      {/* Create branch dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Branch</DialogTitle>
            <DialogDescription>
              Create a new branch for indexing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="branchName">Name (internal)</Label>
              <Input
                id="branchName"
                placeholder="main-branch"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gitBranchName">Git Branch Name</Label>
              <Input
                id="gitBranchName"
                placeholder="main"
                value={formData.gitBranchName}
                onChange={(e) => setFormData({ ...formData, gitBranchName: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                The actual git ref name (e.g., main, develop, feature/x)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Branch"
        description={`Are you sure you want to delete branch "${deleteTarget?.name}"? This will also remove its index data.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />

      {/* Index status dialog */}
      <Dialog open={!!statusBranch} onOpenChange={(open) => !open && setStatusBranch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Index Status</DialogTitle>
            <DialogDescription>
              {statusBranch ? `Branch: ${statusBranch.name}` : ''}
            </DialogDescription>
          </DialogHeader>
          {statusBranch && repoId && (
            <IndexStatusDetail repoId={repoId} branchId={statusBranch._id} branch={statusBranch} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Index Status Detail ───────────────────────────────────────────────────

function IndexStatusDetail({
  repoId,
  branchId,
  branch,
}: {
  repoId: string;
  branchId: string;
  branch: Branch;
}) {
  const { data: status, isLoading } = useGetIndexStatusQuery({ repoId, branchId });

  if (isLoading) {
    return <LoadingSpinner className="py-8" />;
  }

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-muted-foreground">Status:</span>{' '}
          <StatusBadge status={status?.status ?? branch.lastIndexStatus} />
        </div>
        <div>
          <span className="text-muted-foreground">Total Files:</span>{' '}
          {status?.totalFiles ?? '—'}
        </div>
        <div>
          <span className="text-muted-foreground">Total Chunks:</span>{' '}
          {status?.totalChunks ?? '—'}
        </div>
        <div>
          <span className="text-muted-foreground">Last Indexed:</span>{' '}
          {status?.lastIndexedAt || branch.lastIndexedAt
            ? format(
                new Date(status?.lastIndexedAt || branch.lastIndexedAt!),
                'MMM d, yyyy HH:mm',
              )
            : '—'}
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Commit SHA:</span>{' '}
          <span className="font-mono text-xs">
            {status?.lastIndexedCommitSha || branch.lastIndexedCommitSha || '—'}
          </span>
        </div>
        {(status?.lastError || branch.lastError) && (
          <div className="col-span-2">
            <span className="text-muted-foreground">Last Error:</span>{' '}
            <span className="text-destructive text-xs">
              {status?.lastError || branch.lastError}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
